import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Icon, TypeAhead } from '@gumtree/ui-library';
import { qaAttribute } from '@gumtree/ui-library/src/utils/qa-service';
import { trackV2 } from '@gumtree/shared/src/util/track-ga-event';
import { trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';
import { GrowthBookFeature } from '@gumtree/shared/src/model/experiment';

import { searchFieldAriaLabel, searchFieldPlaceholderText } from '../constants';
import type { ShellUseDispatch } from '../../../reducers/common';
import { trackClickSearchBar } from '../search-bar-actions';
import { useClientGrowthbookFeatureValue } from '../../../growthbook/init-growthbook';

interface KeywordSearchFieldProps {
    clearAllFromHistory: () => void;
    useRecentHistory: boolean;
    setKeywordAndUpdateSuggestions: (value: string) => void;
    loadRecentKeywordHistory: () => void;
    query?: string;
    removeSingleFromHistory: (text: string) => void;
    selectSuggestion: (
        option: {
            name: string;
            category?: string;
            categoryDisplayName?: string;
        },
        useRecentHistory: boolean
    ) => void;
    outsideFocus?: boolean;
    suggestions: { name: string; category?: string; categoryDisplayName?: string }[];
}

const KeywordSearchField: React.FC<KeywordSearchFieldProps> = ({
    clearAllFromHistory,
    useRecentHistory,
    setKeywordAndUpdateSuggestions,
    loadRecentKeywordHistory,
    query = '',
    removeSingleFromHistory,
    selectSuggestion,
    suggestions = [],
    outsideFocus,
}) => {
    const dispatch = useDispatch() as ShellUseDispatch;
    const [hasTrackedKeywordHistoryShown, setHasTrackedKeywordHistoryShown] = useState(false);
    const [renderOptions, setRenderOptions] = useState<typeof suggestions>([]);
    const withoutCategory = useClientGrowthbookFeatureValue(
        GrowthBookFeature.SUG_WITHOUT_CATEGORY,
        'A'
    );

    const debounceRef = useRef<number | undefined>(undefined);
    const DEBOUNCE_MS = 250;

    const handleChange = useCallback(
        (value: string) => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
            debounceRef.current = window.setTimeout(() => {
                setKeywordAndUpdateSuggestions(value);
            }, DEBOUNCE_MS);
        },
        [setKeywordAndUpdateSuggestions]
    );

    useEffect(
        () => () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        },
        []
    );

    useEffect(() => {
        loadRecentKeywordHistory();
    }, [loadRecentKeywordHistory]);

    useEffect(() => {
        let rafId: number | null = null;
        let timerId: number | null = null;
        setRenderOptions([]);
        timerId = window.setTimeout(() => {
            rafId = window.requestAnimationFrame(() => {
                setRenderOptions(suggestions);
            });
        }, 0);
        return () => {
            if (rafId) {
                window.cancelAnimationFrame(rafId);
            }
            if (timerId) {
                window.clearTimeout(timerId);
            }
        };
    }, [suggestions]);

    const mappedOptionsOptimised = useMemo(
        () => {
            return renderOptions;
        },
        [renderOptions]
    );

    const handleOpen = useCallback(() => {
        if (useRecentHistory && !hasTrackedKeywordHistoryShown) {
            window.setTimeout(() => {
                trackV2('KeywordHistoryShown');
                trackGA4Event<GA4.RecentSearchHistoryShown>({
                    event: 'recent_search_history_shown',
                });
                setHasTrackedKeywordHistoryShown(true);
            }, 0);
        }
    }, [useRecentHistory, hasTrackedKeywordHistoryShown]);

    const inputProps = {
        'aria-label': searchFieldAriaLabel,
        placeholder: searchFieldPlaceholderText,
        maxLength: 50,
        ...qaAttribute('search-keyword-field'),
    };

    return (
        <TypeAhead
            inputProps={inputProps}
            icon={<Icon type="magnifying-glass" size="medium" />}
            id="q"
            limit={withoutCategory === 'B' ? 30 : 10}
            outsideFocus={outsideFocus}
            name="q"
            onFocus={() => {
                window.setTimeout(() => {
                    dispatch(trackClickSearchBar('term', suggestions.length));
                }, 0);
            }}
            onOpen={handleOpen}
            onChange={handleChange}
            onSelectOption={(value) => {
                selectSuggestion(value, useRecentHistory);
            }}
            options={mappedOptionsOptimised}
            removeSingleFromHistory={removeSingleFromHistory}
            clearAllFromHistory={clearAllFromHistory}
            useRecentHistory={useRecentHistory}
            defaultValue={query}
            isTypeAheadOptionsIconsVisible
            typeAheadIcon={
                useRecentHistory ? (
                    <Icon type="clock" size="medium" />
                ) : (
                    <Icon type="magnifying-glass" size="medium" />
                )
            }
            titleOfRecentHistory={`Recent Search${suggestions.length === 1 ? '' : 'es'}`}
            showClearAllButton
        />
    );
};

export default KeywordSearchField;
